import React from "react";
import {
  Compress,
  Expand,
  AutoFixHigh,
  Person,
  Groups,
  Refresh,
} from "@mui/icons-material";
import { ConfirmDialog } from "../../../shared/ui";

export type OperationType =
  | "condense"
  | "expatiate"
  | "toFirstPerson"
  | "toThirdPerson"
  | "addContext"
  | null;

interface NarrativeConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  operationType: OperationType;
}

const OPERATION_DETAILS: Record<
  Exclude<OperationType, null> | "default",
  { title: string; description: string; icon: React.ReactNode; confirmLabel: string }
> = {
  condense: {
    title: "Condense Narrative",
    description:
      "AI will rewrite this narrative to be more concise while preserving all important details.",
    icon: <Compress />,
    confirmLabel: "Condense",
  },
  expatiate: {
    title: "Expand Narrative",
    description: "AI will rewrite this narrative to be more detailed and comprehensive.",
    icon: <Expand />,
    confirmLabel: "Expand",
  },
  toFirstPerson: {
    title: "Convert to 1st Person",
    description:
      'AI will rewrite this narrative from a first-person perspective, using "I" and "my" throughout.',
    icon: <Person />,
    confirmLabel: "Convert to 1st Person",
  },
  toThirdPerson: {
    title: "Convert to 3rd Person",
    description:
      "AI will rewrite this narrative from a third-person perspective, referring to the officer by name.",
    icon: <Groups />,
    confirmLabel: "Convert to 3rd Person",
  },
  addContext: {
    title: "Regenerate with Context",
    description:
      "AI will regenerate this narrative incorporating the additional context you provided.",
    icon: <Refresh />,
    confirmLabel: "Regenerate",
  },
  default: {
    title: "Regenerate Narrative",
    description: "AI will regenerate this narrative.",
    icon: <AutoFixHigh />,
    confirmLabel: "Confirm",
  },
};

export const NarrativeConfirmationModal: React.FC<NarrativeConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  operationType,
}) => {
  const details = OPERATION_DETAILS[operationType ?? "default"];

  const handleConfirmWithHaptic = () => {
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    onConfirm();
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirmWithHaptic}
      tone="primary"
      icon={details.icon}
      title={details.title}
      description={details.description}
      confirmLabel={details.confirmLabel}
      confirmStartIcon={operationType ? details.icon : undefined}
    />
  );
};
